// --- Free gift cart promotion -------------------------------------------
// Automatically adds (and removes) a free gift product when the cart's
// qualifying subtotal crosses a configurable threshold. The gift can be made
// free via either:
//   1. An automatic discount in Shopify Admin (leave discountCode blank), or
//   2. A discount code applied via /cart/update.js when discountCode is set.
//
// Config is injected by layout/theme.liquid as window.cartGiftPromo:
//   { enable, threshold, variantId, discountCode? }
(function () {
  var cfg = window.cartGiftPromo;
  if (!cfg || !cfg.enable || !cfg.variantId) return;

  var thresholdCents = Math.round(Number(cfg.threshold) * 100);
  var variantId = String(cfg.variantId);
  var discountCode = cfg.discountCode ? String(cfg.discountCode).trim() : "";
  if (!thresholdCents || isNaN(thresholdCents)) return;

  var reconciling = false;
  var pending = false;

  var JSON_HEADERS = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  // The auto-added gift is tagged with this property so it stays a separate
  // line from any units the customer purchases of the same variant.
  var GIFT_PROPERTY = "_free_gift";

  function isFreeGift(item) {
    return !!(item.properties && item.properties[GIFT_PROPERTY]);
  }

  // Subtotal of everything except the auto-added free gift line. Units the
  // customer buys of the same variant still count toward the threshold.
  function qualifyingTotal(cart) {
    return cart.items.reduce(function (sum, item) {
      return isFreeGift(item) ? sum : sum + item.final_line_price;
    }, 0);
  }

  function findGift(cart) {
    return cart.items.filter(function (item) {
      return String(item.variant_id) === variantId && isFreeGift(item);
    })[0];
  }

  function hasDiscountCode(cart, code) {
    if (!code) return false;
    var target = code.toLowerCase();

    function matchesApp(app) {
      if (!app) return false;
      var title = app.title ? String(app.title).toLowerCase() : "";
      var appCode = app.code ? String(app.code).toLowerCase() : "";
      return title === target || appCode === target;
    }

    var cartApps = cart.cart_level_discount_applications || [];
    if (cartApps.some(matchesApp)) return true;

    return cart.items.some(function (item) {
      return (item.line_level_discount_allocations || []).some(function (allocation) {
        return matchesApp(allocation.discount_application) || matchesApp(allocation);
      });
    });
  }

  function getCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(
      function (r) {
        return r.json();
      }
    );
  }

  function changeLine(key, quantity) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: JSON_HEADERS,
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
  }

  function addGift() {
    var properties = {};
    properties[GIFT_PROPERTY] = "true";
    return fetch("/cart/add.js", {
      method: "POST",
      headers: JSON_HEADERS,
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1, properties: properties }],
      }),
    });
  }

  function setDiscount(code) {
    return fetch("/cart/update.js", {
      method: "POST",
      headers: JSON_HEADERS,
      body: JSON.stringify({ discount: code || "" }),
    });
  }

  // Push the latest cart into the Vue drawer without changing its open state.
  // Retries briefly in case the theme runtime hasn't booted yet on first load.
  function refreshDrawer(cart, attempt) {
    attempt = attempt || 0;
    if (window.wetheme && typeof window.wetheme.toggleRightDrawer === "function") {
      var isOpen = document.body.classList.contains("js-drawer-open-right");
      window.wetheme.toggleRightDrawer("cart", isOpen, { cart: cart });
    } else if (attempt < 20) {
      setTimeout(function () {
        refreshDrawer(cart, attempt + 1);
      }, 250);
    }
  }

  function reconcileCart(cart) {
    var qualifies = qualifyingTotal(cart) >= thresholdCents;
    var gift = findGift(cart);
    var touched = false;
    var chain = Promise.resolve();

    function mark(promise) {
      touched = true;
      return promise;
    }

    if (!qualifies) {
      if (gift) {
        chain = chain.then(function () {
          return mark(changeLine(gift.key, 0));
        });
      }
      if (discountCode && hasDiscountCode(cart, discountCode)) {
        chain = chain.then(function () {
          return mark(setDiscount(""));
        });
      }
      return chain.then(function () {
        return touched;
      });
    }

    if (!gift) {
      chain = chain.then(function () {
        return mark(addGift());
      });
    } else if (gift.quantity > 1) {
      chain = chain.then(function () {
        return mark(changeLine(gift.key, 1));
      });
    }

    if (discountCode) {
      chain = chain.then(function () {
        return getCart().then(function (freshCart) {
          if (hasDiscountCode(freshCart, discountCode)) return null;
          touched = true;
          return setDiscount(discountCode);
        });
      });
    }

    return chain.then(function () {
      return touched;
    });
  }

  function reconcile() {
    if (reconciling) {
      pending = true;
      return;
    }
    reconciling = true;

    getCart()
      .then(reconcileCart)
      .then(function (mutated) {
        // Re-fetch so we always refresh the drawer with the full cart object
        // (cart/add.js returns only the added line item).
        if (mutated) return getCart();
        return null;
      })
      .then(function (cart) {
        if (cart) refreshDrawer(cart);
      })
      .catch(function (err) {
        console.error("[CartGiftPromo]", err);
      })
      .then(function () {
        reconciling = false;
        if (pending) {
          pending = false;
          setTimeout(reconcile, 0);
        }
      });
  }

  // Trigger a reconcile after any cart mutation the theme performs via fetch.
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === "string" ? input : (input && input.url) || "";
    var result = origFetch.apply(this, arguments);
    if (!reconciling && CART_MUTATION.test(url)) {
      result
        .then(function (res) {
          if (res && res.ok) setTimeout(reconcile, 0);
        })
        .catch(function () {});
    }
    return result;
  };

  // Reconcile once on load in case the cart already qualifies.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      setTimeout(reconcile, 0);
    });
  } else {
    setTimeout(reconcile, 0);
  }
})();
